// ProjectsMap — a react-map-gl Map showing all saved projects as pins,
// coloured by SEIDR score band (via tierForIndex). The selected pin gets a
// larger ring; a dashed LineString links the selected site to its nearest
// substation when coordinates are available (nearest_grid_lat/lon).
//
// Lazy-mount: this is the SECOND Mapbox instance in the app (the first is
// SiteScreener's). We only mount it after `active` first turns true — i.e.
// on the user's first navigation to #/projects — and keep it mounted
// afterwards so state (bounds, style) persists across tab switches.
//
// Reads MAPBOX_TOKEN from the same env var SiteScreener uses.
// Style id matches SiteScreener's default (light-v11); if you want a
// different style just for Projects, add a config file — do not hardcode.

import React, { useEffect, useRef, useState } from 'react';
import Map, { Source, Layer, Marker, NavigationControl, ScaleControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { tierForIndex } from '../SeidrIndex';
import { humanizeSubstationName } from '../../utils/format';
import { SiteHoverCard, hoverPlacement } from '../map/HoverCards';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN;
const MAP_STYLES = {
  streets:   'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

function toGeoJson(projects) {
  return {
    type: 'FeatureCollection',
    features: (projects || [])
      .filter(p => p.lat != null && p.lon != null)
      .map(p => ({
        type: 'Feature',
        id: p.id,
        properties: {
          id: p.id,
          site_label: p.site_label || '',
          score: p.readiness_score != null ? Math.round(p.readiness_score) : null,
          color: p.readiness_score != null ? tierForIndex(p.readiness_score).color : '#94a3b8',
        },
        geometry: { type: 'Point', coordinates: [Number(p.lon), Number(p.lat)] },
      })),
  };
}

// Build a ~50 m radius circle polygon around a point as a fallback when
// OSM doesn't return a real substation footprint. Uses a flat equirect
// approximation — fine at substation scale, wrong past ~1 km.
function circleAroundPoint(lon, lat, radiusM = 50, steps = 24) {
  const R = 6378137;
  const dLat = (radiusM / R) * (180 / Math.PI);
  const dLon = dLat / Math.cos((lat * Math.PI) / 180);
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    coords.push([lon + dLon * Math.cos(t), lat + dLat * Math.sin(t)]);
  }
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: { fallback: true },
      geometry: { type: 'Polygon', coordinates: [coords] },
    }],
  };
}

// Build a FeatureCollection from an Overpass API `out geom` response.
// Handles ways (polygon-ish) and multipolygon relations.
function overpassToGeoJson(overpass) {
  const features = [];
  for (const el of overpass?.elements || []) {
    if (el.type === 'way' && Array.isArray(el.geometry) && el.geometry.length >= 3) {
      const coords = el.geometry.map(p => [p.lon, p.lat]);
      // Close ring if the way isn't closed already.
      const first = coords[0], last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);
      features.push({
        type: 'Feature',
        properties: { osm_id: el.id, source: 'osm' },
        geometry: { type: 'Polygon', coordinates: [coords] },
      });
    } else if (el.type === 'relation' && Array.isArray(el.members)) {
      const outer = [];
      for (const m of el.members) {
        if (m.role !== 'outer' || !Array.isArray(m.geometry)) continue;
        const coords = m.geometry.map(p => [p.lon, p.lat]);
        if (coords.length >= 3) {
          const first = coords[0], last = coords[coords.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) coords.push(first);
          outer.push([coords]);
        }
      }
      if (outer.length) {
        features.push({
          type: 'Feature',
          properties: { osm_id: el.id, source: 'osm' },
          geometry: { type: 'MultiPolygon', coordinates: outer },
        });
      }
    }
  }
  return features.length ? { type: 'FeatureCollection', features } : null;
}

function selectedLink(project) {
  // Nearest substation coords, if any, drawn as a dashed line to the parcel.
  const e = project?.enrichment || {};
  const gLat = e.nearest_grid_lat, gLon = e.nearest_grid_lon;
  if (project?.lat == null || project?.lon == null || gLat == null || gLon == null) return null;
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [Number(project.lon), Number(project.lat)],
            [Number(gLon),        Number(gLat)],
          ],
        },
      },
    ],
  };
}

/**
 * @param {Object}  props
 * @param {boolean} props.active           — true when #/projects* is showing
 * @param {Array}   props.projects
 * @param {string?} props.selectedSiteId
 * @param {(id:string)=>void}   props.onSelectSite
 * @param {(project:object)=>void=} props.onOpenSite  — pin click. When
 *   omitted the click just selects; when supplied the map treats the
 *   pin as a link to that site's deep-analysis view.
 */
export default function ProjectsMap({ active, projects, selectedSiteId, onSelectSite, onOpenSite }) {
  const mapRef = useRef(null);
  // Lazy-mount: only render the Map once `active` has ever been true.
  const [hasMounted, setHasMounted] = useState(active);
  useEffect(() => { if (active && !hasMounted) setHasMounted(true); }, [active, hasMounted]);

  const [mapReady, setMapReady] = useState(false);
  // Hovered pin: {id, x, y, w}. The card reads the project by id; pin
  // features carry only id, label, score and colour.
  const [hoverPin, setHoverPin] = useState(null);
  const [didFitBounds, setDidFitBounds] = useState(false);
  const [mapStyle, setMapStyle] = useState(MAP_STYLES.satellite);
  const [selectedParcel, setSelectedParcel] = useState(null);        // GeoJSON polygon
  const [substationGeom, setSubstationGeom] = useState(null);        // GeoJSON polygon

  const selectedProject = projects?.find(p => p.id === selectedSiteId) || null;

  // Link endpoints: substation (blue) + nearest harbour (green). Coordinates
  // come from the enrichment when present (nearest_grid_lat/lon,
  // _offtake.nearest_port.lat/lon); otherwise /api/v1/sites/locate resolves
  // them by name so search-saved sites get lines too. (2026-10-05)
  const [linkPts, setLinkPts] = useState({ sub: null, port: null });
  useEffect(() => {
    const p = selectedProject;
    setLinkPts({ sub: null, port: null });
    if (!p || p.lat == null || p.lon == null) return;
    const e = p.enrichment || {};
    const apiBase = import.meta.env?.VITE_API_URL || '';
    let cancelled = false;
    // Never hand Mapbox a non-finite coordinate — a Marker at (NaN, NaN)
    // throws and takes the whole Projects page down (seen 2026-10-05).
    const finite = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
    const put = (k, v) => {
      const lat = finite(v?.lat), lon = finite(v?.lon);
      if (lat == null || lon == null) return;
      if (!cancelled) setLinkPts(prev => ({ ...prev, [k]: { ...v, lat, lon } }));
    };
    const locate = (kind, name) => fetch(`${apiBase}/api/v1/sites/locate?${new URLSearchParams({ kind, name, lat: String(p.lat), lon: String(p.lon) })}`)
      .then(r => (r.ok ? r.json() : null)).catch(() => null);
    const subName = e.nearest_grid_name || e.nearest_grid?.name;
    const kv = e.nearest_grid_voltage_kv;
    const subLabel = subName ? (kv != null ? `${subName} · ${kv} kV` : subName) : 'Substation';
    if (e.nearest_grid_lat != null && e.nearest_grid_lon != null) {
      put('sub', { lat: Number(e.nearest_grid_lat), lon: Number(e.nearest_grid_lon), label: subLabel });
    } else if (subName) {
      locate('substation', subName).then(h => { if (h) put('sub', { lat: h.lat, lon: h.lon, label: `${subLabel} · ${h.distance_km.toFixed(1)} km` }); });
    }
    const op = e._offtake?.nearest_port;
    const portName = op?.name || e.nearest_port_name || e.port_name;
    if (op && op.lat != null && op.lon != null) {
      put('port', { lat: op.lat, lon: op.lon, label: `${op.name} · ${op.distance_km} km` });
    } else if (portName) {
      locate('port', portName).then(h => { if (h) put('port', { lat: h.lat, lon: h.lon, label: `${h.name} · ${h.distance_km.toFixed(1)} km` }); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedProject?.enrichment?._offtake?.nearest_port?.name]);

  // Fetch the parcel geometry when the selection changes. Reuses the
  // /parcel-at-point endpoint that SiteScreener already hits. Non-blocking:
  // if the fetch fails, we just don't draw the polygon (the pin + dashed
  // link still render).
  //
  // We CLEAR to null on selection change (so the old parcel doesn't
  // linger under the new pin), but we do NOT clear on empty response —
  // that used to nuke a good polygon whenever a stale StrictMode re-mount
  // re-fired and hit a cache miss.
  useEffect(() => {
    if (!selectedProject || selectedProject.lat == null || selectedProject.lon == null) {
      setSelectedParcel(null);
      return;
    }
    setSelectedParcel(null);
    const apiBase = import.meta.env?.VITE_API_URL || '';
    const controller = new AbortController();
    const url = `${apiBase}/api/v1/layers/parcel-at-point?lng=${selectedProject.lon}&lat=${selectedProject.lat}`;
    console.info('[ProjectsMap] fetching parcel', { id: selectedProject.id, url });
    fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then(r => {
        console.info('[ProjectsMap] parcel response', { id: selectedProject.id, ok: r.ok, status: r.status });
        return r.ok ? r.json() : null;
      })
      .then(fc => {
        if (controller.signal.aborted) return;
        console.info('[ProjectsMap] parcel body', { id: selectedProject.id, hasFeatures: !!fc?.features?.length });
        if (!fc || !fc.features || !fc.features.length) {
          console.warn('[ProjectsMap] parcel-at-point returned no features for', selectedProject.id);
          return;
        }
        setSelectedParcel({ type: 'FeatureCollection', features: [fc.features[0]] });
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('[ProjectsMap] parcel-at-point fetch failed:', err);
        }
      });
    return () => controller.abort();
  }, [selectedProject?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the actual substation footprint from OSM Overpass when a site is
  // selected. Falls back to a small circle around the point when the
  // substation is stored as a node in OSM (no polygon) or when Overpass is
  // unreachable, so the map always has a visible blue marker area for the
  // substation. Non-blocking.
  useEffect(() => {
    const gLat = linkPts.sub?.lat;
    const gLon = linkPts.sub?.lon;
    if (gLat == null || gLon == null) { setSubstationGeom(null); return; }

    // Snap the fallback in immediately so the analyst sees SOMETHING while
    // Overpass is being queried. Real polygon replaces it if found.
    setSubstationGeom(circleAroundPoint(Number(gLon), Number(gLat)));

    const controller = new AbortController();
    // Overpass QL: substation ways + relations within 300 m of the point.
    // Nodes are skipped — they have no polygon; the fallback circle covers
    // that case anyway.
    const query = `[out:json][timeout:8];
(
  way(around:300,${gLat},${gLon})["power"="substation"];
  relation(around:300,${gLat},${gLon})["power"="substation"];
);
out geom;`;
    fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (controller.signal.aborted) return;
        const fc = overpassToGeoJson(data);
        if (fc) setSubstationGeom(fc);
        // If Overpass has no polygon here, keep the fallback circle in
        // place — the analyst still sees the blue marker area.
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.warn('[ProjectsMap] Overpass substation fetch failed:', err);
        }
      });
    return () => controller.abort();
  }, [selectedProject?.id, linkPts.sub?.lat, linkPts.sub?.lon]);  // eslint-disable-line react-hooks/exhaustive-deps

  // fitBounds runs once projects arrive AND the map is ready — not on mount,
  // per Q6 in the plan. Later selections use easeTo (see below). Bounds are
  // computed manually to avoid an ES-module dance around mapbox-gl's
  // LngLatBounds constructor.
  useEffect(() => {
    if (!mapReady || didFitBounds) return;
    if (!Array.isArray(projects) || projects.length === 0) return;
    const coords = projects
      .filter(p => p.lat != null && p.lon != null)
      .map(p => [Number(p.lon), Number(p.lat)]);
    if (!coords.length) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    if (coords.length === 1) {
      map.easeTo({ center: coords[0], zoom: 12, duration: 0 });
    } else {
      const lngs = coords.map(c => c[0]);
      const lats = coords.map(c => c[1]);
      const bounds = [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
      map.fitBounds(bounds, {
        padding: { top: 60, right: 60, bottom: 60, left: 60 },
        maxZoom: 12,
        duration: 0,
      });
    }
    setDidFitBounds(true);
  }, [mapReady, projects, didFitBounds]);

  // On selection, easeTo the pin — don't zoom past parcel level.
  useEffect(() => {
    if (!mapReady || !selectedProject || selectedProject.lat == null) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    const currentZoom = map.getZoom();
    map.easeTo({
      center: [Number(selectedProject.lon), Number(selectedProject.lat)],
      zoom: Math.max(currentZoom, 11),
      duration: 500,
    });
  }, [mapReady, selectedProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Inline height/position style guards against a race where the CSS
  // module hasn't been injected yet — without it the map div collapses to
  // zero height and only the legend shows. Height inherits from the grid
  // cell (.pv2-top-map is height:100% of .pv2-top), with a floor.
  const shellStyle = { position: 'relative', width: '100%', height: '100%', minHeight: 300, overflow: 'hidden' };

  if (!hasMounted) {
    // Reserve height so the layout doesn't jump when the map mounts.
    return <div className="pm-shell pm-placeholder" style={shellStyle} />;
  }

  const points = toGeoJson(projects);
  const link = (() => {
    if (!selectedProject || selectedProject.lat == null) return null;
    const from = [Number(selectedProject.lon), Number(selectedProject.lat)];
    const feats = [];
    if (linkPts.sub)  feats.push({ type: 'Feature', properties: { color: '#3b82f6', id: 'sub' },  geometry: { type: 'LineString', coordinates: [from, [linkPts.sub.lon,  linkPts.sub.lat]] } });
    if (linkPts.port) feats.push({ type: 'Feature', properties: { color: '#10b981', id: 'port' }, geometry: { type: 'LineString', coordinates: [from, [linkPts.port.lon, linkPts.port.lat]] } });
    return feats.length ? { type: 'FeatureCollection', features: feats } : null;
  })();

  // On satellite the label stroke reads better in white; on streets keep
  // the dark halo for contrast against the pale background.
  const isSat = mapStyle === MAP_STYLES.satellite;

  return (
    <div className="pm-shell" style={shellStyle}>
      <Map
        ref={mapRef}
        initialViewState={{ longitude: 22.9, latitude: 62.8, zoom: 6 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_TOKEN}
        onLoad={() => setMapReady(true)}
        onStyleData={() => setMapReady(true)}  // ensure fit re-runs after style swap
        interactiveLayerIds={['projects-pins']}
        onMouseMove={(e) => {
          const f = e.features?.[0];
          setHoverPin(f?.properties?.id != null
            ? { id: String(f.properties.id), x: e.point.x, y: e.point.y, w: e.target?.getContainer?.()?.clientWidth || null }
            : null);
        }}
        onMouseLeave={() => setHoverPin(null)}
        onClick={(e) => {
          const f = e.features?.[0];
          const id = f?.properties?.id;
          if (!id) return;
          if (onSelectSite) onSelectSite(String(id));
          if (onOpenSite) {
            const project = projects?.find(p => String(p.id) === String(id));
            if (project) onOpenSite(project);
          }
        }}
      >
        {/* Mapbox built-in controls — zoom / compass / scale. Positioned
            top-left; style toggle sits top-right so they don't overlap. */}
        <NavigationControl position="top-left" showCompass={false} />
        <ScaleControl position="bottom-right" unit="metric" />

        {/* Selected parcel polygon — GREEN (the site itself). */}
        {selectedParcel && (
          <Source id="selected-parcel" type="geojson" data={selectedParcel}>
            <Layer
              id="selected-parcel-fill"
              type="fill"
              paint={{
                'fill-color': '#22c55e',
                'fill-opacity': 0.35,
              }}
            />
            <Layer
              id="selected-parcel-outline"
              type="line"
              paint={{
                'line-color': '#bbf7d0',
                'line-width': 3,
              }}
            />
          </Source>
        )}

        {/* Selected substation polygon — BLUE (grid infrastructure).
            Real OSM footprint when available, ~50 m circle fallback so
            the marker always has a visible area. */}
        {substationGeom && (
          <Source id="selected-substation" type="geojson" data={substationGeom}>
            <Layer
              id="selected-substation-fill"
              type="fill"
              paint={{
                'fill-color': '#3b82f6',
                'fill-opacity': 0.32,
              }}
            />
            <Layer
              id="selected-substation-outline"
              type="line"
              paint={{
                'line-color': '#60a5fa',
                'line-width': 2,
              }}
            />
          </Source>
        )}

        {link && (
          <Source id="selected-link" type="geojson" data={link}>
            <Layer
              id="selected-link-line"
              type="line"
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 2.5,
                'line-dasharray': [2, 2],
                'line-opacity': 0.95,
              }}
            />
          </Source>
        )}
        {[linkPts.sub ? { ...linkPts.sub, color: '#3b82f6', id: 'sub' } : null,
          linkPts.port ? { ...linkPts.port, color: '#10b981', id: 'port' } : null].filter(Boolean).map(l => (
          <Marker key={l.id} longitude={l.lon} latitude={l.lat} anchor="bottom">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
              <div style={{ background: 'rgba(15,23,42,0.92)', color: l.color, border: `1px solid ${l.color}`, borderRadius: 6,
                            padding: '2px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 3 }}>
                {l.label}
              </div>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: l.color, border: '2px solid #fff', boxShadow: `0 0 0 4px ${l.color}55` }} />
            </div>
          </Marker>
        ))}

        <Source id="projects-src" type="geojson" data={points}>
          {/* Selection ring — sits under the pin; only rendered for the selected id */}
          <Layer
            id="projects-ring"
            type="circle"
            filter={selectedSiteId ? ['==', ['get', 'id'], selectedSiteId] : ['==', ['get', 'id'], '__none__']}
            paint={{
              'circle-radius': 14,
              'circle-color': 'transparent',
              'circle-stroke-color': '#2563eb',
              'circle-stroke-width': 2,
            }}
          />
          {/* Pins — coloured by score-band, black outline for contrast on light basemap */}
          <Layer
            id="projects-pins"
            type="circle"
            paint={{
              'circle-radius': ['case', ['==', ['get', 'id'], selectedSiteId || '__none__'], 9, 7],
              'circle-color': ['get', 'color'],
              'circle-stroke-color': '#0f172a',
              'circle-stroke-width': 1.5,
            }}
          />
          {/* Score number, in white for readability against the coloured pin */}
          <Layer
            id="projects-score-label"
            type="symbol"
            layout={{
              'text-field': ['coalesce', ['to-string', ['get', 'score']], ''],
              'text-size': 10,
              'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
              'text-allow-overlap': true,
              'text-ignore-placement': true,
            }}
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': 'rgba(0,0,0,0.45)',
              'text-halo-width': 0.6,
            }}
          />
        </Source>

        {/* The substation name/voltage chip that used to sit here read
            enrichment.nearest_grid_lat/lon directly and crashed Mapbox with
            (NaN, NaN) once the dashed line could come from the locator
            instead (2026-10-05). The labelled endpoint markers above carry
            name · kV · distance, so the chip is gone. */}
        {hoverPin && (() => {
          const project = projects?.find(p => String(p.id) === hoverPin.id);
          if (!project) return null;
          // A saved site's enrichment is the search result it was saved
          // from, so the card shows the same facts as on the search map.
          const site = { ...(project.enrichment || {}), site_label: project.site_label,
                         readiness_score: project.readiness_score ?? project.enrichment?.readiness_score };
          return (
            <div className="map-hover"
              style={{ left: hoverPin.x, top: hoverPin.y, ...hoverPlacement(hoverPin.x, hoverPin.y, hoverPin.w) }}>
              <SiteHoverCard site={site} hint="Click to open it on the search map" />
            </div>
          );
        })()}
      </Map>

      {/* Style switcher (streets / satellite). Satellite is default because
          the user wants to see the actual parcel on aerial imagery. */}
      <div className="pm-style-toggle" role="group" aria-label="Map style">
        <button
          type="button"
          className={mapStyle === MAP_STYLES.streets ? 'active' : ''}
          onClick={() => setMapStyle(MAP_STYLES.streets)}
          aria-pressed={mapStyle === MAP_STYLES.streets}
        >Map</button>
        <button
          type="button"
          className={mapStyle === MAP_STYLES.satellite ? 'active' : ''}
          onClick={() => setMapStyle(MAP_STYLES.satellite)}
          aria-pressed={mapStyle === MAP_STYLES.satellite}
        >Satellite</button>
      </div>

      {/* Small overlay legend. Score bands (pin colour) + selection areas. */}
      <div className="pm-legend">
        <span className="pm-legend-row"><span className="pm-dot" style={{ background: '#22c55e' }} /> Suitable 75+</span>
        <span className="pm-legend-row"><span className="pm-dot" style={{ background: '#d97706' }} /> Conditional 50–74</span>
        <span className="pm-legend-row"><span className="pm-dot" style={{ background: '#ef4444' }} /> Weak &lt;50</span>
        <span className="pm-legend-row">
          <span className="pm-swatch" style={{ background: 'rgba(34, 197, 94, 0.35)', borderColor: '#4ade80' }} />
          Selected site (parcel)
        </span>
        <span className="pm-legend-row">
          <span className="pm-swatch" style={{ background: 'rgba(59, 130, 246, 0.35)', borderColor: '#60a5fa' }} />
          Substation
        </span>
      </div>
    </div>
  );
}
